import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, ChevronDown, Loader2, Printer, Radar, Trophy } from "lucide-react";
import api, { fmtNum, fmtPrice, formatApiError, raceTime, RECENT } from "@/lib/api";
import { RaceTypeBadge } from "@/components/race/RaceTypeBadge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { PrintOptions } from "@/components/PrintOptions";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { FinalSheet, SortToggle } from "@/components/race/FinalSheet";

const STATES = ["NSW", "VIC", "QLD", "SA", "WA", "TAS", "ACT", "NT"];
export const auDate = (o) => new Date(Date.now() + o * 86400000).toLocaleDateString("en-CA", { timeZone: "Australia/Sydney" });
export const dayLabel = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "2-digit", month: "short" });
const toggle = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
export const PREF_BADGE = "ml-2 align-middle whitespace-nowrap text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500 text-[#080C14] shadow-[0_0_12px_rgba(16,185,129,0.45)]";

const norm = (t) => (t || "").toLowerCase().replace(/\s+/g, " ").trim();
export const trackWas = (r) => (r?.track_initial && r.track_condition && norm(r.track_initial) !== norm(r.track_condition) ? r.track_initial : null);
export const TrackChange = ({ from, to, testId }) => (from ? (
  <span className="inline-flex items-center whitespace-nowrap text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/40" data-testid={testId}>
    Track changed: {from} → {to}
  </span>) : null);

export const byMeeting = (races) => {
  const groups = {};
  races.forEach((r) => { const k = `${r.venue}|${r.state || ""}`; (groups[k] = groups[k] || []).push(r); });
  return Object.entries(groups)
    .map(([key, list]) => {
      const latest = [...list].sort((a, b) => (b.race_number || 0) - (a.race_number || 0)).find((r) => r.track_condition);
      return { key, venue: list[0].venue, state: list[0].state, track: latest?.track_condition, trackFrom: trackWas(latest), races: [...list].sort((a, b) => (a.race_number || 0) - (b.race_number || 0)) };
    })
    .sort((a, b) => (Math.min(...a.races.map((r) => r.start_aest_iso ? Date.parse(r.start_aest_iso) : Infinity)) - Math.min(...b.races.map((r) => r.start_aest_iso ? Date.parse(r.start_aest_iso) : Infinity))));
};

export const FeatureButton = ({ f, on, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`ml-2 px-3.5 py-2 rounded border text-sm font-mono font-bold transition-colors shadow-[0_0_18px_rgba(251,191,36,0.25)] ${on
      ? "bg-amber-400 border-amber-300 text-slate-950" : "bg-amber-500/15 border-amber-400/70 text-amber-200 hover:bg-amber-500/25"}`}>
    <Trophy className="h-4 w-4 mr-1.5 inline -mt-0.5" />{f.label} • {dayLabel(f.date)}
  </button>
);

export const Chip = ({ on, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`px-3.5 py-2 rounded border text-sm font-mono transition-colors ${on ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-slate-700 text-slate-400 hover:text-white"}`}>
    {children}
  </button>
);

const AutoRefresh = ({ onRefresh }) => {
  const { user, setUser } = useAuth();
  const on = !!user?.auto_refresh;
  useEffect(() => {
    if (!on) return;
    const t = setInterval(onRefresh, 5 * 60000);
    return () => clearInterval(t);
  }, [on]); // eslint-disable-line react-hooks/exhaustive-deps
  const flip = async (v) => {
    try {
      await api.put("/auto-refresh", { on: v });
      setUser({ ...user, auto_refresh: v });
      toast.success(v ? "Auto refresh on: fields, track & scratchings update every 30 min during racing" : "Auto refresh off");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <label className="inline-flex items-center gap-2 mr-3 text-xs text-slate-400 cursor-pointer" title="Rescans races that haven't started every 30 minutes during racing hours">
      <Switch checked={on} onCheckedChange={flip} data-testid="program-auto-refresh-switch" />
      <span className="eyebrow">Auto refresh • 30 min</span>
    </label>
  );
};

const ScanPanel = ({ onDone, date, setDate, updatedAt, onRefresh, features = [], feature, setFeature }) => {
  const [states, setStates] = useState(STATES);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);

  const run = async () => {
    setBusy(true);
    setMsg("Downloading race & field details...");
    try {
      const { data } = await api.post("/scan/australia", { states, dates: [date] });
      timer.current = setInterval(async () => {
        const { data: j } = await api.get(`/parse/jobs/${data.job_id}`);
        const p = j.progress;
        if (j.status === "running") {
          return setMsg({ download: `Downloading ${p ? `${p.done}/${p.total} meetings • ${p.label}` : "meetings..."}`, rate: "Scoring every horse...", model: "Applying rating rules → Rating → $ price..." }[j.stage] || "Working...");
        }
        clearInterval(timer.current);
        setBusy(false);
        if (j.status === "error") return setMsg(null) || toast.error(j.error || "Scan failed");
        setMsg(j.warning ? `Partial scan: ${j.warning}` : null);
        toast.success(`${j.saved_ids.length} race(s) scanned & rated`,
          j.skipped_started ? { description: `${j.skipped_started} race(s) already started were kept as they were (pre-race data).` } : undefined);
        onDone(date);
      }, 2500);
    } catch (e) {
      setBusy(false);
      setMsg(null);
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="panel p-5 space-y-4 no-print" data-testid="program-scan-panel">
      <div className="flex flex-wrap items-center gap-2">
        <span className="eyebrow mr-2">States to scan</span>
        {STATES.map((s) => <Chip key={s} on={states.includes(s)} onClick={() => setStates(toggle(states, s))} testId={`program-state-${s}`}>{s}</Chip>)}
        <button type="button" onClick={() => setStates(states.length === STATES.length ? [] : STATES)} className="text-xs text-slate-400 hover:text-white ml-1" data-testid="program-states-all">{states.length === STATES.length ? "Clear" : "All"}</button>
        {features.map((f, i) => <FeatureButton key={f.label} f={f} on={feature?.label === f.label} onClick={() => setFeature(f)} testId={`program-feature-${i}`} />)}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="eyebrow mr-2">Race day</span>
        {[0, 1].map((o) => <Chip key={o} on={date === auDate(o) && !feature} onClick={() => setDate(auDate(o))} testId={`program-day-${o}`}>{["Today", "Tomorrow"][o]} • {dayLabel(auDate(o))}</Chip>)}
        <div className="flex-1" />
        <AutoRefresh onRefresh={onRefresh} />
        {updatedAt && <span className="text-[11px] num text-slate-500" data-testid="program-last-updated">Last updated {updatedAt}</span>}
        <Button onClick={run} disabled={busy || !states.length} data-testid="program-scan-button">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Radar className="h-4 w-4 mr-2" />}{busy ? "Scanning..." : "Scan & Rate"}
        </Button>
      </div>
      {msg && <div className="text-xs num text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2" data-testid="program-scan-status">{msg}</div>}
    </div>
  );
};

const RaceMarket = ({ race, i, onUpdated }) => {
  const [v, setV] = useState(race.market_pct ?? 100);
  useEffect(() => setV(race.market_pct ?? 100), [race.market_pct]);
  const save = async () => {
    const n = v === "" ? 100 : Number(v);
    if (v === "") setV(100);
    if (n === (race.market_pct ?? 100)) return;
    if (n < 50 || n > 300) return toast.error("Market % must be between 50 and 300");
    try {
      const { data } = await api.put(`/races/${race.id}/market`, { market_pct: n });
      onUpdated(data);
      toast.success(`Race priced to ${n}% market`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <label className="inline-flex items-center gap-2 text-xs text-slate-400" onClick={(e) => e.stopPropagation()}>
      <span className="eyebrow">Market %</span>
      <input type="number" min={50} max={300} placeholder="100" value={v} onChange={(e) => setV(e.target.value)} onBlur={save}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} data-testid={`program-market-${i}`}
        className="w-20 bg-[#0F172A] border border-slate-700 rounded px-2 py-1 num text-white focus:border-emerald-500 outline-none" />
    </label>
  );
};

export const RaceDetailBody = ({ race, i, onUpdated, prefix = "program" }) => {
  const [sort, setSort] = useState("rating");
  const [expandAll, setExpandAll] = useState(false);
  const field = race.evaluation.ratings || [];
  return (
    <div className="px-1 sm:px-4 pb-5 space-y-4" data-testid={`${prefix}-race-detail-${i}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4">
          <SortToggle value={sort} onChange={setSort} testId={`${prefix}-sort-${i}`} />
          <RaceMarket race={race} i={i} onUpdated={onUpdated} />
        </div>
        <Button size="sm" variant={expandAll ? "default" : "outline"} onClick={() => setExpandAll(!expandAll)} data-testid={`${prefix}-expand-form-toggle-${i}`}>
          {expandAll ? "Collapse form" : "Click to expand form"}
        </Button>
      </div>
      <FinalSheet key={expandAll ? "all" : "one"} race={race} ratings={field} sort={sort} testId={`${prefix}-sheet-${i}`} form expandAll={expandAll}
        firstStarters={race.evaluation.first_starters} scratched={(race.horses || []).filter((h) => h.scratched)} />
    </div>
  );
};

const RaceRow = ({ race, i, open, onOpen, marked, onMark, onUpdated }) => {
  const top = race.evaluation.ratings?.[0];
  return (
    <div className={`panel transition-colors ${race.evaluation.preferred ? "bg-emerald-500/[0.06] shadow-[inset_4px_0_0_#10B981]" : ""} ${marked ? "border-amber-500/50" : race.evaluation.preferred ? "border-emerald-500/60" : ""}`} data-testid={`program-race-${i}`}>
      <div className="flex items-center gap-3 sm:gap-4 p-4 cursor-pointer" onClick={onOpen}>
        <span onClick={(e) => e.stopPropagation()}><Checkbox checked={marked} onCheckedChange={onMark} data-testid={`program-race-mark-${i}`} title="Highlight for printing" /></span>
        <span className="num text-amber-400 font-bold w-24 shrink-0 whitespace-nowrap" data-testid={`program-race-time-${i}`}>{raceTime(race)}</span>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold">{race.venue} R{race.race_number}
            {race.evaluation.preferred && <span className={PREF_BADGE} data-testid={`program-preferred-badge-${i}`}>Preferred Race</span>}
            <span className="ml-2"><RaceTypeBadge type={race.race_type} testId={`program-race-type-${i}`} /></span>
          </div>
          <div className="text-xs text-slate-400 truncate">{[race.race_class, race.distance && `${fmtNum(race.distance, 0)}m`, `${race.evaluation.field_size} runners`, race.track_condition].filter(Boolean).join(" • ")}</div>
        </div>
        {top && <div className="hidden sm:block text-right text-sm"><span className="text-slate-500 text-xs mr-2">Top</span><span className="text-white">{top.number} {top.name}</span> <span className="num text-amber-400 font-bold ml-1">{fmtPrice(top.price)}</span></div>}
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && <RaceDetailBody race={race} i={i} onUpdated={onUpdated} />}
    </div>
  );
};

const sameVenue = (a, b) => (a || "").toLowerCase().replace(/ (park|racecourse)$/, "") === (b || "").toLowerCase().replace(/ (park|racecourse)$/, "");

const AbandonedCard = ({ a, i }) => (
  <div className="panel p-5 opacity-80 border-rose-500/40" data-testid={`program-abandoned-card-${i}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="font-display text-2xl font-bold text-slate-300 line-through decoration-rose-400/70">{a.venue}</div>
      <span className="text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/40">{a.transferred_to ? "Transferred" : "Abandoned"}</span>
    </div>
    <div className="text-xs text-slate-400 mt-4" data-testid={`program-abandoned-note-${i}`}>{a.transferred_to ? `Meeting transferred to ${a.transferred_to}.` : "Meeting abandoned today. No races to rate."}</div>
  </div>
);

export const MeetingCards = ({ groups, onOpen, prefix = "program", abandoned = [] }) => (
  <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid={`${prefix}-meeting-cards`}>
    {abandoned.filter((a) => a.all && !groups.some((g) => sameVenue(g.venue, a.venue))).map((a, i) => <AbandonedCard key={a.venue} a={a} i={i} />)}
    {groups.map((g, gi) => {
      const pref = g.races.filter((r) => r.evaluation.preferred).length;
      const first = g.races.find((r) => r.start_aest);
      const gone = abandoned.find((a) => sameVenue(g.venue, a.venue));
      return (
        <button key={g.key} type="button" onClick={() => onOpen(g.key)} data-testid={`${prefix}-meeting-card-${gi}`}
          className={`panel p-5 text-left transition-colors hover:border-emerald-500/60 group ${pref ? "shadow-[inset_4px_0_0_#10B981]" : ""}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="font-display text-2xl font-bold text-white" data-testid={`${prefix}-meeting-name-${gi}`}>{g.venue}</div>
              <div className="text-xs text-slate-400 mt-0.5">{[g.state, g.track].filter(Boolean).join(" • ")}</div>
              {g.trackFrom && <div className="mt-2"><TrackChange from={g.trackFrom} to={g.track} testId={`${prefix}-meeting-track-change-${gi}`} /></div>}
            </div>
            <ArrowUpRight className="h-5 w-5 text-slate-500 group-hover:text-emerald-400 transition-colors" />
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-xs font-mono text-slate-400">
            <span><span className="text-white">{g.races.length}</span> races</span>
            {pref > 0 && <span className="text-emerald-300">{pref} preferred</span>}
            {first && <span>First <span className="text-amber-400">{raceTime(first)}</span></span>}
            {gone && <span className="text-rose-300" data-testid={`${prefix}-meeting-abandoned-${gi}`}>{gone.all ? "Meeting abandoned" : `${gone.races.map((n) => `R${n}`).join(", ")} abandoned`}</span>}
          </div>
        </button>
      );
    })}
  </div>
);

const MeetingMarket = ({ g, gi, onUpdated }) => {
  const [v, setV] = useState("");
  const apply = async () => {
    const n = Number(v);
    if (v === "" || n < 50 || n > 300) return toast.error("Market % must be between 50 and 300");
    try {
      const { data } = await api.post("/races/market", { race_ids: g.races.map((r) => r.id), market_pct: n });
      data.forEach(onUpdated);
      toast.success(`All ${data.length} races at ${g.venue} priced to ${n}% market`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="ml-auto inline-flex items-center gap-2 text-xs text-slate-400" data-testid={`program-meeting-market-${gi}`}>
      <span className="eyebrow">Meeting Market %</span>
      <input type="number" min={50} max={300} placeholder="100" value={v} onChange={(e) => setV(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && apply()} data-testid={`program-meeting-market-input-${gi}`}
        className="w-20 bg-[#0F172A] border border-slate-700 rounded px-2 py-1 num text-white focus:border-emerald-500 outline-none" />
      <Button size="sm" variant="outline" onClick={apply} data-testid={`program-meeting-market-apply-${gi}`}>Apply to all races</Button>
    </div>
  );
};

const lastUpdated = (races) => {
  const t = Math.max(...races.map((r) => Date.parse(r.updated_at || r.refreshed_at || r.created_at) || 0));
  return t ? new Date(t).toLocaleString("en-AU", { weekday: "short", day: "2-digit", month: "short", hour: "numeric", minute: "2-digit", timeZoneName: "short" }) : null;
};

const MeetingView = ({ g, gi, onBack, marked, setMarked, onUpdated }) => {
  const [opened, setOpened] = useState([]);
  const ids = g.races.map((r) => r.id);
  const all = ids.every((id) => marked.includes(id));
  const updatedAt = lastUpdated(g.races);
  return (
    <section className="space-y-3" data-testid={`program-meeting-${gi}`}>
      <div className="flex flex-wrap items-center gap-3 border-b border-slate-800 pb-3">
        <div className="flex flex-col gap-1">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="program-back-button"><ArrowLeft className="h-4 w-4 mr-1.5" />Back to Race Program</Button>
          {updatedAt && <span className="text-[11px] num text-slate-500" data-testid={`program-meeting-updated-${gi}`}>Last updated {updatedAt}</span>}
        </div>
        <Checkbox checked={all} onCheckedChange={() => setMarked(all ? marked.filter((id) => !ids.includes(id)) : [...new Set([...marked, ...ids])])}
          data-testid={`program-meeting-select-${gi}`} title="Select all races in this meeting for printing" />
        <h2 className="font-display text-2xl font-bold text-white" data-testid={`program-meeting-title-${gi}`}>
          {[g.venue, g.state, g.track, `${g.races.length} races`].filter(Boolean).join(" • ")}
        </h2>
        <TrackChange from={g.trackFrom} to={g.track} testId={`program-meeting-view-track-change-${gi}`} />
        <MeetingMarket g={g} gi={gi} onUpdated={onUpdated} />
      </div>
      {g.races.map((r, j) => (
        <RaceRow key={r.id} race={r} i={j} open={opened.includes(r.id)} onOpen={() => setOpened(toggle(opened, r.id))}
          marked={marked.includes(r.id)} onMark={() => setMarked(toggle(marked, r.id))} onUpdated={onUpdated} />
      ))}
    </section>
  );
};

const PrintSheet = ({ race, order }) => (
  <section className="print-race">
    <div className="print-race-head">
      <b>{raceTime(race)} • {race.venue} R{race.race_number}</b>
      <span>{[race.race_class, race.distance && `${fmtNum(race.distance, 0)}m`, `${race.evaluation.field_size} runners`, race.track_condition].filter(Boolean).join(" • ")}</span>
      {race.evaluation.preferred && <span className="print-preferred">PREFERRED RACE</span>}
      {race.race_type && <span className="print-preferred">{race.race_type.toUpperCase()}</span>}
    </div>
    <FinalSheet race={race} ratings={race.evaluation.ratings || []} sort={order} testId="print-sheet"
      firstStarters={race.evaluation.first_starters} scratched={(race.horses || []).filter((h) => h.scratched)} />
  </section>
);

export default function Program() {
  const [races, setRaces] = useState(null);
  const [day, setDay] = useState(auDate(0));
  const [meeting, setMeeting] = useState(null);
  const [marked, setMarked] = useState([]);
  const [printing, setPrinting] = useState(null);
  const [printDialog, setPrintDialog] = useState(null);
  const seq = useRef(0);
  const load = (d) => { const my = ++seq.current; return api.get("/races", { params: RECENT() })
    .then((r) => { if (my !== seq.current) return; setRaces(r.data); if (d) { setDay(d); setMeeting(null); } })
    .catch((e) => toast.error(formatApiError(e))); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [abandoned, setAbandoned] = useState([]);
  const [features, setFeatures] = useState([]);
  const [feature, setFeature] = useState(null);
  useEffect(() => { api.get("/featured").then((r) => setFeatures(r.data)).catch(() => setFeatures([])); }, []);
  useEffect(() => { api.get("/abandoned", { params: { date: day } }).then((r) => setAbandoned(feature ? [] : r.data)).catch(() => setAbandoned([])); }, [day, races, feature]);
  useEffect(() => {
    if (!printing) return;
    const t = setTimeout(() => { window.print(); setPrinting(null); }, 300);
    return () => clearTimeout(t);
  }, [printing]);

  const dayRaces = (races || []).filter((r) => r.date === day && (!feature || r.featured === feature.label));
  const groups = useMemo(() => byMeeting(dayRaces), [races, day, feature]); // eslint-disable-line react-hooks/exhaustive-deps
  const picked = groups.find((g) => g.key === meeting);
  const shown = picked ? picked.races : groups.flatMap((g) => g.races);
  const ticked = shown.filter((r) => marked.includes(r.id)).map((r) => r.id);
  const updated = (race) => { seq.current++; setRaces((list) => list.map((r) => (r.id === race.id ? race : r))); };
  const open = (k) => { setMeeting(k); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div className="space-y-6">
      <div className="fade-up no-print">
        <div className="eyebrow mb-2" data-testid="program-view-mode">Race program • {feature ? `${feature.label} • ` : ""}{dayLabel(day)}{picked ? ` • ${picked.venue}` : " • meetings"}</div>
        <h1 className="text-4xl sm:text-5xl font-bold" data-testid="program-title">Race Program</h1>
      </div>
      {!picked && <ScanPanel onDone={(d) => load(d)} onRefresh={() => load()} date={day} updatedAt={dayRaces.length ? lastUpdated(dayRaces) : null}
        setDate={(d) => { setFeature(null); setDay(d); setMeeting(null); }} features={features} feature={feature}
        setFeature={(f) => { setFeature(f); setDay(f.date); setMeeting(null); }} />}
      {!races ? <div className="py-12 grid place-items-center"><div className="loader-bar" /></div> : (
        <div className="space-y-4 no-print">
          <div className="flex flex-wrap items-center gap-2">
            <div className="eyebrow" data-testid="program-day-summary">{groups.length} meeting{groups.length === 1 ? "" : "s"} • {dayRaces.length} races • {dayLabel(day)}</div>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => setPrintDialog("all")} disabled={!shown.length} data-testid="program-print-all"><Printer className="h-4 w-4 mr-2" />{picked ? "Print meeting" : "Print full program"}</Button>
            <Button onClick={() => setPrintDialog("ticked")} disabled={!ticked.length} data-testid="program-print-marked"><Printer className="h-4 w-4 mr-2" />Print selected ({ticked.length})</Button>
          </div>
          {!groups.length && !abandoned.length && <div className="panel p-10 text-center text-slate-400" data-testid="program-empty">{feature
            ? `The ${feature.label} field is being downloaded and rated. It updates every 30 minutes until the race jumps.`
            : <>No meetings downloaded for this day yet. Choose states and press Scan &amp; Rate.</>}</div>}
          {picked
            ? <MeetingView g={picked} gi={groups.indexOf(picked)} onBack={() => setMeeting(null)} marked={marked} setMarked={setMarked} onUpdated={updated} />
            : <MeetingCards groups={groups} onOpen={open} abandoned={abandoned} />}
        </div>
      )}
      <PrintOptions open={!!printDialog} preset={printDialog} onClose={() => setPrintDialog(null)} meetings={picked ? [picked] : groups} shown={shown} ticked={ticked}
        onPrint={(p) => { setPrintDialog(null); setPrinting(p); }} />
      {printing && (
        <div className="print-block" data-testid="program-print-area">
          <h1 className="text-2xl font-bold mb-4">Race Program — {dayLabel(day)}{printing.label}</h1>
          {printing.races.map((r) => <PrintSheet key={r.id} race={r} order={printing.order} />)}
        </div>
      )}
    </div>
  );
}
